import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { X, Sparkles, Check, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * iter303ak-fix79 — AI-assisted "needs city clarification" cleanup queue.
 *
 * Feeds the operator a batch of clients whose city is blank or a generic
 * region label (e.g. "Waikato"), asks OpenStreetMap Nominatim for the
 * real township based on street+suburb, and lets the operator eyeball
 * suggestions before writing them back to the DB.
 *
 * Rate-limited to 1 req/sec on the server side per Nominatim's terms —
 * a 20-row batch takes ~40 seconds (query + optional fallback).
 */
export default function CityCleanupModal({ open, onClose, onApplied }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [rows, setRows] = useState([]);
  const [batchSize, setBatchSize] = useState(50);
  const [checked, setChecked] = useState({});          // client_id → bool
  const [edits, setEdits] = useState({});              // client_id → suggested override
  const [error, setError] = useState('');
  const [totalQueued, setTotalQueued] = useState(0);

  const runPreview = async () => {
    setLoading(true);
    setError('');
    try {
      // First get the queue count so we can show progress context.
      const { data: listData } = await api.get(`/clients?needs_city_review=true&limit=1`);
      setTotalQueued(listData.total || 0);

      const { data } = await api.post('/clients/geocode-cities/preview', {
        client_ids: [],
        limit: batchSize,
        require_street: true,          // skip unfixable no-street rows
      }, { timeout: (batchSize + 5) * 2000 });   // ~2s per row worst-case
      setRows(data.results || []);
      // Auto-check rows where a suggestion exists AND it differs from current.
      const next = {};
      const nextEdits = {};
      (data.results || []).forEach(r => {
        const same = (r.suggested_city || '').toLowerCase() === (r.current_city || '').toLowerCase();
        if (r.suggested_city && !same) next[r.client_id] = true;
        nextEdits[r.client_id] = r.suggested_city || '';
      });
      setChecked(next);
      setEdits(nextEdits);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || e.message || 'Preview failed');
    }
    setLoading(false);
  };

  const applyAccepted = async () => {
    const updates = rows
      .filter(r => checked[r.client_id])
      .map(r => ({ client_id: r.client_id, new_city: (edits[r.client_id] || '').trim() }))
      .filter(u => u.new_city);
    if (!updates.length) {
      toast.error('Nothing selected to apply');
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post('/clients/geocode-cities/apply', { updates });
      toast.success(`${data.updated} client cities updated`);
      onApplied?.();
      // Drop applied rows from the current view; keep unapplied.
      const appliedSet = new Set(updates.map(u => u.client_id));
      setRows(prev => prev.filter(r => !appliedSet.has(r.client_id)));
      setChecked({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Apply failed');
    }
    setApplying(false);
  };

  // Auto-kick off a preview when the modal first opens.
  useEffect(() => {
    if (open && !rows.length && !loading) runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const selectedCount = Object.values(checked).filter(Boolean).length;
  const suggestedCount = rows.filter(r => r.suggested_city).length;
  const failedCount = rows.filter(r => !r.suggested_city).length;

  return (
    <div
      data-testid="city-cleanup-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col border border-[#E2E2DF]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E2DF] bg-gradient-to-r from-orange-50/60 to-white">
          <div className="flex items-center gap-2.5">
            <Sparkles size={18} className="text-[#D35400]" />
            <div>
              <h2 className="text-base font-extrabold tracking-tight text-slate-800 font-[Manrope]">
                AI City Cleanup
              </h2>
              <p className="text-[11px] text-slate-500 font-medium">
                Nominatim geocoder · rate-limited 1 req/sec · shows only rows with a street address · operator confirms every write
              </p>
            </div>
          </div>
          <button
            data-testid="city-cleanup-close"
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded transition-colors"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E2DF] bg-slate-50/40 text-xs flex-wrap gap-3">
          <div className="flex items-center gap-3 text-slate-600">
            <span data-testid="city-cleanup-queue-count">
              Queue: <span className="font-mono-num font-bold text-slate-800">{totalQueued}</span> clients need city clarification
            </span>
            {rows.length > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span>Batch of <span className="font-mono-num font-bold text-slate-700">{rows.length}</span></span>
                <span className="text-emerald-700">✓ {suggestedCount} suggested</span>
                {failedCount > 0 && (
                  <span className="text-rose-600">✗ {failedCount} no hit</span>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-slate-500">Batch:</label>
            <select
              data-testid="city-cleanup-batch-size"
              value={batchSize}
              onChange={e => setBatchSize(parseInt(e.target.value))}
              className="px-2 py-1 border border-[#E2E2DF] rounded bg-white text-xs font-mono-num"
              disabled={loading}
            >
              <option value={10}>10 (~20s)</option>
              <option value={20}>20 (~40s)</option>
              <option value={50}>50 (~100s)</option>
              <option value={100}>100 (~200s)</option>
            </select>
            <button
              data-testid="city-cleanup-refresh"
              onClick={runPreview}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E2E2DF] rounded font-semibold text-slate-700 hover:border-slate-400 disabled:opacity-40 transition-colors"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Fetching…' : 'Fetch batch'}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {error && (
            <div className="m-4 flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded text-xs text-rose-800">
              <AlertTriangle size={14} /> {error}
            </div>
          )}
          {loading && rows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 text-sm">
              <RefreshCw size={22} className="animate-spin mb-3 text-[#D35400]" />
              <p>Geocoding {batchSize} addresses via Nominatim…</p>
              <p className="text-xs text-slate-400 mt-1">Rate-limited to 1 req/sec (~{batchSize}s)</p>
            </div>
          )}
          {!loading && rows.length === 0 && !error && (
            <div className="text-center py-16 text-slate-400 text-sm">
              <p>No suggestions yet — click "Fetch batch" to start.</p>
            </div>
          )}
          {rows.length > 0 && (
            <table className="w-full text-xs" data-testid="city-cleanup-results-table">
              <thead className="sticky top-0 bg-slate-50/95 backdrop-blur border-b border-[#E2E2DF]">
                <tr>
                  <th className="w-10 px-3 py-2.5">
                    <input
                      type="checkbox"
                      data-testid="city-cleanup-select-all"
                      checked={rows.length > 0 && rows.filter(r => r.suggested_city).every(r => checked[r.client_id])}
                      onChange={e => {
                        const next = {};
                        if (e.target.checked) {
                          rows.forEach(r => { if (r.suggested_city) next[r.client_id] = true; });
                        }
                        setChecked(next);
                      }}
                    />
                  </th>
                  <th className="text-left px-2 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 w-16">C#</th>
                  <th className="text-left px-2 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">Name</th>
                  <th className="text-left px-2 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">Address</th>
                  <th className="text-left px-2 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 w-24">Current</th>
                  <th className="text-left px-2 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 w-48">Suggested City</th>
                  <th className="text-left px-2 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Nominatim Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const noHit = !r.suggested_city;
                  const sameAsCurrent = (r.suggested_city || '').toLowerCase() === (r.current_city || '').toLowerCase();
                  return (
                    <tr
                      key={r.client_id}
                      data-testid={`city-cleanup-row-${r.client_id}`}
                      className={`border-t border-[#E2E2DF] ${noHit ? 'bg-rose-50/30' : sameAsCurrent ? 'bg-slate-50/40' : 'hover:bg-orange-50/20'}`}
                    >
                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          data-testid={`city-cleanup-check-${r.client_id}`}
                          checked={!!checked[r.client_id]}
                          disabled={noHit}
                          onChange={e => setChecked(prev => ({ ...prev, [r.client_id]: e.target.checked }))}
                        />
                      </td>
                      <td className="px-2 py-2 align-top font-mono-num font-bold text-[#D35400]">
                        C{r.client_no}
                      </td>
                      <td className="px-2 py-2 align-top text-slate-800 font-medium">{r.name || '—'}</td>
                      <td className="px-2 py-2 align-top text-slate-600">
                        <div>{r.address_num} {r.address_street}</div>
                        <div className="text-slate-400">{r.address_suburb}</div>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${(r.current_city || '').toLowerCase() === 'waikato' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>
                          {r.current_city || '(blank)'}
                        </span>
                      </td>
                      <td className="px-2 py-2 align-top">
                        {noHit ? (
                          <span className="text-rose-600 text-[11px] italic">
                            no hit {r.error ? `· ${r.error}` : ''}
                          </span>
                        ) : (
                          <input
                            type="text"
                            data-testid={`city-cleanup-edit-${r.client_id}`}
                            value={edits[r.client_id] ?? r.suggested_city}
                            onChange={e => setEdits(prev => ({ ...prev, [r.client_id]: e.target.value }))}
                            className="w-full px-2 py-1 border border-[#E2E2DF] rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#D35400]"
                          />
                        )}
                        {r.used_fallback && !noHit && (
                          <span className="block text-[9px] text-amber-700 mt-0.5">⚠ suburb-level fallback</span>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top text-[10px] text-slate-400 hidden lg:table-cell max-w-[280px] truncate" title={r.source_display}>
                        {r.source_display || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-[#E2E2DF] bg-slate-50/40">
          <div className="text-xs text-slate-500">
            <span className="font-mono-num font-bold text-slate-800">{selectedCount}</span> selected
            {selectedCount > 0 && <span className="ml-2 text-slate-400">· review before applying</span>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 border border-[#E2E2DF] rounded text-xs font-semibold text-slate-600 hover:bg-white"
            >
              Close
            </button>
            <button
              data-testid="city-cleanup-apply"
              onClick={applyAccepted}
              disabled={applying || selectedCount === 0}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-[#D35400] hover:bg-[#A04000] text-white rounded text-xs font-bold disabled:opacity-40 transition-colors"
            >
              <Check size={13} /> {applying ? 'Applying…' : `Apply ${selectedCount} selected`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
